import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { 
  FileText, Calendar, Download, Users, Clock, 
  AlertTriangle, TrendingUp, Wallet, User, BarChart3 
} from "lucide-react";
import { useBusiness } from "@/contexts/BusinessContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AttendanceReports = () => {
  const { selectedBusinessId } = useBusiness();
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState("daily");
  const [reportData, setReportData] = useState(null);
  
  // Filters
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  const fetchReport = async () => {
    setLoading(true);
    setReportData(null);

    try {
      let url;
      if (reportType === "daily") {
        url = `${BACKEND_URL}/api/attendance/reports/daily?date=${selectedDate}`;
      } else if (reportType === "monthly") {
        url = `${BACKEND_URL}/api/attendance/reports/monthly?month=${selectedMonth}&year=${selectedYear}`;
      } else if (reportType === "salary") {
        url = `${BACKEND_URL}/api/attendance/reports/salary-summary?month=${selectedMonth}&year=${selectedYear}`;
      }

      if (selectedBusinessId) {
        url += `&business_id=${selectedBusinessId}`;
      }

      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        toast.error("Failed to fetch report");
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      toast.error("Error loading report");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatTime = (isoString) => {
    if (!isoString) return "--:--";
    return new Date(isoString).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const downloadCSV = () => {
    if (!reportData) return;

    let csvContent = "";
    let filename = "";

    if (reportType === "daily") {
      csvContent = "Name,Role,Status,Punch In,Punch Out,Working Hours,Late,Overtime\n";
      reportData.records.forEach(r => {
        csvContent += `"${r.name}","${r.role}","${r.status}","${formatTime(r.punch_in)}","${formatTime(r.punch_out)}","${r.working_hours}","${r.late_minutes}","${r.overtime_hours}"\n`;
      });
      filename = `attendance_${selectedDate}.csv`;
    } else if (reportType === "monthly") {
      csvContent = "Name,Role,Total Days,Present,Half Days,Absent,Leave,Late Count,Overtime Hours\n";
      reportData.records.forEach(r => {
        csvContent += `"${r.name}","${r.role}","${r.total_days}","${r.present_days}","${r.half_days}","${r.absent_days}","${r.leave_days}","${r.late_count}","${r.overtime_hours}"\n`;
      });
      filename = `attendance_${months.find(m => m.value === selectedMonth)?.label}_${selectedYear}.csv`;
    } else if (reportType === "salary") {
      csvContent = "Name,Working Days,Present,Paid Leave Used,Gross Salary,Deductions,Net Payable,Status\n";
      reportData.slips.forEach(s => {
        csvContent += `"${s.user_name}","${s.working_days}","${s.present_days}","${s.paid_leave_used ?? 0}","${s.gross_salary}","${s.total_deductions}","${s.net_payable}","${s.payment_status}"\n`;
      });
      filename = `salary_${months.find(m => m.value === selectedMonth)?.label}_${selectedYear}.csv`;
    }

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Report downloaded successfully");
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="attendance-reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <BarChart3 className="w-8 h-8 text-emerald-600" />
            Attendance Reports
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Generate and download attendance reports
          </p>
        </div>
      </div>

      {/* Report Type Selection */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card 
          className={`cursor-pointer transition-all ${
            reportType === "daily" 
              ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20" 
              : "border-slate-200 dark:border-slate-700 hover:border-emerald-300"
          }`}
          onClick={() => setReportType("daily")}
        >
          <CardContent className="p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              reportType === "daily" ? "bg-emerald-100" : "bg-slate-100 dark:bg-slate-800"
            }`}>
              <Calendar className={`w-5 h-5 ${reportType === "daily" ? "text-emerald-600" : "text-slate-500"}`} />
            </div>
            <div>
              <p className={`font-medium ${reportType === "daily" ? "text-emerald-700" : ""}`}>
                Daily Report
              </p>
              <p className="text-xs text-slate-500">Single day attendance</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${
            reportType === "monthly" 
              ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20" 
              : "border-slate-200 dark:border-slate-700 hover:border-emerald-300"
          }`}
          onClick={() => setReportType("monthly")}
        >
          <CardContent className="p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              reportType === "monthly" ? "bg-emerald-100" : "bg-slate-100 dark:bg-slate-800"
            }`}>
              <Users className={`w-5 h-5 ${reportType === "monthly" ? "text-emerald-600" : "text-slate-500"}`} />
            </div>
            <div>
              <p className={`font-medium ${reportType === "monthly" ? "text-emerald-700" : ""}`}>
                Monthly Report
              </p>
              <p className="text-xs text-slate-500">Full month summary</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${
            reportType === "salary" 
              ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20" 
              : "border-slate-200 dark:border-slate-700 hover:border-emerald-300"
          }`}
          onClick={() => setReportType("salary")}
        >
          <CardContent className="p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              reportType === "salary" ? "bg-emerald-100" : "bg-slate-100 dark:bg-slate-800"
            }`}>
              <Wallet className={`w-5 h-5 ${reportType === "salary" ? "text-emerald-600" : "text-slate-500"}`} />
            </div>
            <div>
              <p className={`font-medium ${reportType === "salary" ? "text-emerald-700" : ""}`}>
                Salary Report
              </p>
              <p className="text-xs text-slate-500">Payroll summary</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Generate */}
      <Card className="border-slate-200 dark:border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            {reportType === "daily" ? (
              <div className="flex-1">
                <Label className="text-xs text-slate-500 mb-1 block">Select Date</Label>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  data-testid="date-input"
                />
              </div>
            ) : (
              <>
                <div className="flex-1">
                  <Label className="text-xs text-slate-500 mb-1 block">Month</Label>
                  <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map(m => (
                        <SelectItem key={m.value} value={m.value.toString()}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label className="text-xs text-slate-500 mb-1 block">Year</Label>
                  <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map(y => (
                        <SelectItem key={y} value={y.toString()}>
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <Button 
              onClick={fetchReport}
              disabled={loading}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="generate-report-btn"
            >
              {loading ? "Loading..." : "Generate Report"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report Results */}
      {reportData && (
        <>
          {/* Summary Cards */}
          {reportType === "daily" && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-slate-200 dark:border-slate-700 p-4">
                <div className="text-center">
                  <Users className="w-6 h-6 mx-auto text-slate-400 mb-2" />
                  <p className="text-2xl font-bold">{reportData.total_staff}</p>
                  <p className="text-xs text-slate-500">Total Staff</p>
                </div>
              </Card>
              <Card className="border-slate-200 dark:border-slate-700 p-4">
                <div className="text-center">
                  <User className="w-6 h-6 mx-auto text-green-500 mb-2" />
                  <p className="text-2xl font-bold text-green-600">{reportData.present}</p>
                  <p className="text-xs text-slate-500">Present</p>
                </div>
              </Card>
              <Card className="border-slate-200 dark:border-slate-700 p-4">
                <div className="text-center">
                  <User className="w-6 h-6 mx-auto text-red-500 mb-2" />
                  <p className="text-2xl font-bold text-red-600">{reportData.absent}</p>
                  <p className="text-xs text-slate-500">Absent</p>
                </div>
              </Card>
              <Card className="border-slate-200 dark:border-slate-700 p-4">
                <div className="text-center">
                  <AlertTriangle className="w-6 h-6 mx-auto text-amber-500 mb-2" />
                  <p className="text-2xl font-bold text-amber-600">{reportData.late}</p>
                  <p className="text-xs text-slate-500">Late</p>
                </div>
              </Card>
            </div>
          )}

          {reportType === "salary" && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-slate-200 dark:border-slate-700 p-4">
                <div className="text-center">
                  <Users className="w-6 h-6 mx-auto text-slate-400 mb-2" />
                  <p className="text-2xl font-bold">{reportData.total_employees}</p>
                  <p className="text-xs text-slate-500">Employees</p>
                </div>
              </Card>
              <Card className="border-slate-200 dark:border-slate-700 p-4">
                <div className="text-center">
                  <Wallet className="w-6 h-6 mx-auto text-blue-500 mb-2" />
                  <p className="text-xl font-bold text-blue-600">{formatCurrency(reportData.total_gross_salary)}</p>
                  <p className="text-xs text-slate-500">Gross Salary</p>
                </div>
              </Card>
              <Card className="border-slate-200 dark:border-slate-700 p-4">
                <div className="text-center">
                  <Wallet className="w-6 h-6 mx-auto text-green-500 mb-2" />
                  <p className="text-xl font-bold text-green-600">{formatCurrency(reportData.total_paid)}</p>
                  <p className="text-xs text-slate-500">Total Paid</p>
                </div>
              </Card>
              <Card className="border-slate-200 dark:border-slate-700 p-4">
                <div className="text-center">
                  <Wallet className="w-6 h-6 mx-auto text-red-500 mb-2" />
                  <p className="text-xl font-bold text-red-600">{formatCurrency(reportData.total_pending)}</p>
                  <p className="text-xs text-slate-500">Pending</p>
                </div>
              </Card>
            </div>
          )}

          {reportType === "salary" && reportData.is_calculated && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                <AlertTriangle className="w-5 h-5" />
                <p className="text-sm font-medium">
                  This report is calculated on the fly. To save permanent salary slips, go to Salary Management and generate slips for each employee.
                </p>
              </div>
            </div>
          )}

          {/* Report Table */}
          <Card className="border-slate-200 dark:border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">
                {reportType === "daily" ? `Daily Attendance - ${selectedDate}` : 
                 reportType === "monthly" ? `Monthly Attendance - ${months.find(m => m.value === selectedMonth)?.label} ${selectedYear}` :
                 `Salary Summary - ${months.find(m => m.value === selectedMonth)?.label} ${selectedYear}`}
              </CardTitle>
              <Button variant="outline" size="sm" onClick={downloadCSV}>
                <Download className="w-4 h-4 mr-2" />
                Download CSV
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                {reportType === "daily" && (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 dark:bg-slate-800">
                        <TableHead>Name</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Punch In</TableHead>
                        <TableHead>Punch Out</TableHead>
                        <TableHead>Hours</TableHead>
                        <TableHead>Late</TableHead>
                        <TableHead>Overtime</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.records?.map((r, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{r.name}</TableCell>
                          <TableCell className="capitalize text-xs">{r.role?.replace("_", " ")}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              r.status === "present" ? "bg-green-100 text-green-700" :
                              r.status === "half_day" ? "bg-blue-100 text-blue-700" :
                              "bg-red-100 text-red-700"
                            }`}>
                              {r.status}
                            </span>
                          </TableCell>
                          <TableCell>{formatTime(r.punch_in)}</TableCell>
                          <TableCell>{formatTime(r.punch_out)}</TableCell>
                          <TableCell>{r.working_hours?.toFixed(1)}h</TableCell>
                          <TableCell className={r.is_late ? "text-amber-600" : ""}>
                            {r.is_late ? `${r.late_minutes}m` : "-"}
                          </TableCell>
                          <TableCell className="text-blue-600">
                            {r.overtime_hours > 0 ? `+${r.overtime_hours.toFixed(1)}h` : "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                {reportType === "monthly" && (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 dark:bg-slate-800">
                        <TableHead>Name</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Days</TableHead>
                        <TableHead>Present</TableHead>
                        <TableHead>Half</TableHead>
                        <TableHead>Absent</TableHead>
                        <TableHead>Leave</TableHead>
                        <TableHead>Late</TableHead>
                        <TableHead>Overtime</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.records?.map((r, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{r.name}</TableCell>
                          <TableCell className="capitalize text-xs">{r.role?.replace("_", " ")}</TableCell>
                          <TableCell>{r.total_days}</TableCell>
                          <TableCell className="text-green-600 font-medium">{r.present_days}</TableCell>
                          <TableCell className="text-blue-600">{r.half_days}</TableCell>
                          <TableCell className="text-red-600 font-medium">{r.absent_days}</TableCell>
                          <TableCell className="text-purple-600">{r.leave_days}</TableCell>
                          <TableCell className="text-amber-600">{r.late_count}</TableCell>
                          <TableCell className="text-blue-600">{r.overtime_hours.toFixed(1)}h</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                {reportType === "salary" && (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 dark:bg-slate-800">
                        <TableHead>Employee</TableHead>
                        <TableHead>Working Days</TableHead>
                        <TableHead>Present</TableHead>
                        <TableHead>Paid Leave Used</TableHead>
                        <TableHead>Gross Salary</TableHead>
                        <TableHead>Deductions</TableHead>
                        <TableHead>Net Payable</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.slips?.map((s) => (
                        <TableRow key={s.slip_id}>
                          <TableCell className="font-medium">{s.user_name}</TableCell>
                          <TableCell>{s.working_days}</TableCell>
                          <TableCell className="text-green-600">{s.present_days}</TableCell>
                          <TableCell className="text-purple-600">{s.paid_leave_used ?? 0}</TableCell>
                          <TableCell>{formatCurrency(s.gross_salary)}</TableCell>
                          <TableCell className="text-red-600">-{formatCurrency(s.total_deductions)}</TableCell>
                          <TableCell className="font-bold text-emerald-600">{formatCurrency(s.net_payable)}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              s.payment_status === "paid" ? "bg-green-100 text-green-700" :
                              s.payment_status === "hold" ? "bg-amber-100 text-amber-700" :
                              "bg-red-100 text-red-700"
                            }`}>
                              {s.payment_status}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default AttendanceReports;
